import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { View, StyleSheet, StyleProp, ViewStyle, TextStyle, TouchableOpacity, Pressable, Dimensions } from 'react-native';
import MyAppText from './MyAppText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

export type ModeType = 'SQUARE' | 'ROUNDED';

export type BadgeColor = 'transparencyBlack' | 'blue' | 'red' | 'orange' | 'green' | 'purple' | 'gray' | 'yellow' | 'teal';

// Define the types for the Badge component's props
type BadgeProps = {
    /**
     * The content to display inside the badge. Can be a number or a string.
     */
    children: React.ReactNode;
    /**
     * The size of the badge. 'small' for compact, 'medium' for standard, 'large' for more prominent.
     * Defaults to 'medium'.
     */
    size?: 'small' | 'medium' | 'large';
    /**
     * Optional style to apply to the badge container (View).
     */
    style?: StyleProp<ViewStyle>;
    /**
     * Optional style to apply to the badge text (Text).
     */
    textStyle?: StyleProp<TextStyle>;
    removable?: boolean;
    onRemove?: () => void;
    mode?: ModeType;
    count?: number;
    color?: BadgeColor;
    /**
     * Optional content shown in a tooltip when hovering the badge (web).
     * When omitted, the badge renders exactly as before.
     */
    tooltip?: string | React.ReactNode;
    /**
     * Side where the tooltip opens. Defaults to 'right'.
     */
    tooltipDirection?: 'left' | 'right';
} & ({
    mode: 'SQUARE';
    /**
     * The background color of the badge. Defaults to a standard primary blue.
     */
    backgroundColor?: undefined;
    /**
     * The color of the text inside the badge. Defaults to white.
     */
    textColor?: undefined;
} | {
    mode: 'ROUNDED';
    /**
     * The background color of the badge. Defaults to a standard primary blue.
     */
    backgroundColor?: string;
    /**
     * The color of the text inside the badge. Defaults to white.
     */
    textColor?: string;
});

const Badge: React.FC<BadgeProps> = ({
    children,
    backgroundColor = '#2196F3',
    color,
    textColor = '#FFFFFF',
    size = 'medium',
    style,
    textStyle,
    removable,
    onRemove,
    mode,
    count,
    tooltip,
    tooltipDirection,
}) => {
    const { styles, theme } = useStyles(styleSheet);

    // Determine size styles based on the 'size' prop
    const sizeStyles = StyleSheet.create({
        small: {
            minWidth: 18,
            minHeight: 22,
            borderRadius: mode === 'ROUNDED' ? 24 : 4,
            paddingHorizontal: 8,
            paddingVertical: 4,
        },
        medium: {
            minWidth: 24,
            minHeight: 24,
            borderRadius: mode === 'ROUNDED' ? 24 : 4,
            paddingHorizontal: 8,
            paddingVertical: 4,
        },
        large: {
            minWidth: 32,
            minHeight: 27,
            borderRadius: mode === 'ROUNDED' ? 24 : 4,
            paddingHorizontal: 8,
            paddingVertical: 4,
        },
    });

    const fontSizeStyles = StyleSheet.create({
        small: {
            fontSize: 10,
        },
        medium: {
            fontSize: 12,
        },
        large: {
            fontSize: 14,
        },
    });

    const badge = (
        <View
            style={[
                styles.badgeContainer(color ? theme.colors.badges[color].backgroundColor : backgroundColor),
                sizeStyles[size],
                { backgroundColor: color ? theme.colors.badges[color].backgroundColor : backgroundColor },
                style, // Allow external style to override or extend
            ]}
        >
            <MyAppText
                style={[
                    styles.badgeText,
                    fontSizeStyles[size],
                    { color: color ? theme.colors.badges[color].textColor : textColor },
                    textStyle, // Allow external textStyle to override or extend
                ]}
            >
                {children}
            </MyAppText>
            {removable &&
                <TouchableOpacity onPress={onRemove}>
                    <FontAwesomeIcon icon={faClose} fontSize={fontSizeStyles[size].fontSize} color={color ? theme.colors.badges[color].textColor : textColor} />
                </TouchableOpacity>}

            {count && count > 1 &&
                <View style={{
                    backgroundColor: color ? theme.colors.badges[color].textColor : textColor,
                    borderRadius: 24,
                    width: sizeStyles[size].minHeight - 6,
                    height: sizeStyles[size].minHeight - 6,
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                    <MyAppText style={{
                        color: color ? theme.colors.badges[color].backgroundColor : backgroundColor,
                        fontSize: fontSizeStyles[size].fontSize,
                    }}>{count}</MyAppText>
                </View>
            }
        </View>
    );

    if (!tooltip) {
        return badge;
    }

    return (
        <BadgeTooltip content={tooltip} direction={tooltipDirection ?? 'right'}>
            {badge}
        </BadgeTooltip>
    );
};

const TOOLTIP_GAP = 8;
const TOOLTIP_MAX_WIDTH = 320;

function BadgeTooltip({ content, direction, children }: { content: string | React.ReactNode; direction: 'left' | 'right'; children: React.ReactNode; }) {
    const { styles } = useStyles(styleSheet);
    const badgeRef = useRef<View>(null);
    const [position, setPosition] = useState<{ top: number; left?: number; right?: number; maxWidth: number; } | null>(null);

    return (
        <>
            <Pressable
                ref={badgeRef}
                style={{ alignSelf: 'flex-start' }}
                onHoverIn={() => {
                    badgeRef.current?.measureInWindow((x, y, width) => {
                        const windowWidth = Dimensions.get('window').width;
                        const top = y + window.scrollY;
                        if (direction === 'left') {
                            setPosition({
                                top,
                                right: windowWidth - x + TOOLTIP_GAP,
                                maxWidth: Math.min(TOOLTIP_MAX_WIDTH, x - TOOLTIP_GAP * 2),
                            });
                        } else {
                            const left = x + width + TOOLTIP_GAP;
                            setPosition({
                                top,
                                left: left + window.scrollX,
                                maxWidth: Math.min(TOOLTIP_MAX_WIDTH, windowWidth - left - TOOLTIP_GAP),
                            });
                        }
                    });
                }}
                onHoverOut={() => setPosition(null)}
            >
                {children}
            </Pressable>
            {position && createPortal(
                <View
                    pointerEvents='none'
                    style={[styles.tooltip, {
                        top: position.top,
                        left: position.left,
                        right: position.right,
                        maxWidth: position.maxWidth,
                    }]}
                >
                    {typeof content === 'string'
                        ? <MyAppText style={styles.tooltipText}>{content}</MyAppText>
                        : content}
                </View>,
                document.body
            )}
        </>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    badgeContainer: (bgColor: string) => ({
        flexDirection: 'row',
        columnGap: 8,
        justifyContent: 'center',
        alignItems: 'center',
        alignSelf: 'flex-start', // Ensures the badge only takes up necessary width/height
        borderWidth: bgColor.toLocaleLowerCase() == '#ffffff' ? 1 : 0,
        borderColor: theme.colors.neutral['100'],
    }),
    badgeText: {
        fontWeight: '600',
        textAlign: 'left',
    },
    tooltip: {
        position: 'absolute',
        padding: 16,
        borderRadius: 12,
        backgroundColor: theme.colors.neutral['500'],
        opacity: 1,
        zIndex: 99999,
    },
    tooltipText: {
        color: theme.colors.white['500'],
        fontSize: 13,
    },
}));

export default Badge;
